import { Box, IconButton, styled, TableCell, TableRow, TableRowProps, Typography } from "@mui/material";
import {
  AttendanceActionEnum,
  AttendanceReconfirmationStatusEnum,
  AttendanceStatusEnum,
  PositionRoleEnum,
  PositionStatusEnum,
} from "@sinch/core/entities/serverEnums";
import { OriginalRoleIcon } from "@sinch/components/OriginalRoleIcon/OriginalRoleIcon";
import { WageCompositionTooltip } from "@sinch/components/application/WageCompositionTooltip/WageCompositionTooltip";
import { MdsMoreVert } from "@sinch/icons";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { MouseEventHandler } from "react";
import { MdsIcon } from "../../../MdsIcon/MdsIcon";
import { AttendanceStatusIcon } from "../../Icons/AttendanceStatusIcon";
import { WorkerListItem } from "../../Worker/WorkerListItem";
import { ActionMenu, ActionMenuActions, MenuAction } from "../ActionMenu/ActionMenu";
import { ShiftPanelDetailFragment } from "../queries.types";
import { TransportsCell } from "../TransportCell";
import { ReconfirmationStatusIcon } from "@sinch/components/application/Icons/ReconfirmationStatusIcon";
import { useFormat } from "@sinch/core/hooks/format";

export const AttendanceRow: React.FC<{
  attendance: ShiftPanelDetailFragment["positions"][0]["attendances"][0];
  currentRole: PositionRoleEnum;
  positionStatus: PositionStatusEnum;
  onUpdate?: () => Promise<any>;
}> = ({ attendance, currentRole, positionStatus }) => {
  const { num } = useFormat();
  return (
    <AttendanceTableRow key={attendance.id}>
      <TableCell colSpan={2} size="small" width={24}>
        <WorkerListItem expectedRating={attendance.rating?.amount} worker={attendance.worker} />
      </TableCell>
      <TableCell size="small">
        <Box
          sx={{
            display: "flex",
            justifyItems: "center",
            gap: 0.5,
          }}
        >
          <AttendanceStatusIcon status={attendance.status as AttendanceStatusEnum} />
          <ReconfirmationStatusIcon status={attendance.reconfirmationStatus} />
          <OriginalRoleIcon currentRole={currentRole} originalRole={attendance.originalRole} />
        </Box>
      </TableCell>
      <TableCell size="small">
        <TransportsCell transportAttendances={attendance.transportAttendances} />
      </TableCell>
      <TableCell align="right" size="small">
        {attendance.wageComposition.components.length > 0 ? (
          <WageCompositionTooltip
            approximate={positionStatus !== PositionStatusEnum.Completed}
            composition={attendance.wageComposition}
          />
        ) : (
          // attendances on the old ratelists have no wage composition, only the plain attendance wage
          <Typography
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            {num.precisionCurrency(attendance.wage ?? 0)}
          </Typography>
        )}
      </TableCell>
      {isNotNilOrEmpty(attendance.allowedActions) && (
        <TableCell size="small">
          <ActionMenuCell
            allowedActions={attendance.allowedActions as AttendanceActionEnum[]}
            attendanceId={attendance.id}
            attendanceReconfirmationStatus={attendance.reconfirmationStatus as AttendanceReconfirmationStatusEnum}
            attendanceStatus={attendance.status as AttendanceStatusEnum}
          />
        </TableCell>
      )}
    </AttendanceTableRow>
  );
};

export const AttendanceTableRow = styled(TableRow)<TableRowProps>(({ theme: { spacing } }) => ({
  padding: spacing(0, 1),
  height: "32px",
  "& .MuiTableCell-root": {
    padding: spacing(0, 0.5),
  },
  "& .MuiTableCell-root:first-of-type": {
    paddingLeft: spacing(2),
  },
}));

const ActionMenuCell: React.FC<{
  attendanceId: string;
  allowedActions: AttendanceActionEnum[];
  attendanceStatus: AttendanceStatusEnum;
  attendanceReconfirmationStatus: AttendanceReconfirmationStatusEnum;
}> = ({ attendanceId, allowedActions, attendanceStatus, attendanceReconfirmationStatus }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} sx={{ padding: 0 }}>
        <MdsIcon color="info" fontSize="small" icon={MdsMoreVert} />
      </IconButton>
      <ActionMenu
        actions={
          [
            attendanceStatus === AttendanceStatusEnum.ConfirmationPending && {
              type: ActionMenuActions.ConfirmInvitation,
              data: {
                attendanceId: attendanceId,
              },
            },
            attendanceReconfirmationStatus === AttendanceReconfirmationStatusEnum.Pending && {
              type: ActionMenuActions.ResendInvitation,
              data: {
                attendanceId: attendanceId,
              },
            },
            ...allowedActions.map((action) => ({
              type: action,
              data: {
                attendanceId: attendanceId,
              },
            })),
          ].filter(Boolean) as MenuAction[]
        }
        anchorEl={anchorEl}
        onClose={handleClose}
      />
    </>
  );
};
